import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Button } from "./ui";
import {
  OPEN_AI_EVENT, TASKS, checklistVisible, laterBannerVisible, onboardingOf, startOnboardingTour,
} from "../lib/onboarding";

const TASK_ROUTES = { alert: "/alerts", watchlist: "/listings", cockpit: "/objekt-cockpit", ai: null };

/** Dashboard block: the "Später" reminder, then the "Erste Schritte" checklist. */
export default function OnboardingChecklist() {
  const { user, updateOnboarding } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  if (!user) return null;
  const ob = onboardingOf(user);

  if (laterBannerVisible(ob)) {
    return (
      <div className="border-b border-border bg-surface px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3"
           data-testid="onboarding-later-banner">
        <div>
          <div className="overline">{t("onboarding.laterEyebrow")}</div>
          <p className="mt-1 text-sm text-text-secondary">{t("onboarding.laterBody")}</p>
        </div>
        <Button onClick={startOnboardingTour} data-testid="onboarding-later-start">{t("onboarding.startShort")}</Button>
      </div>
    );
  }

  if (!checklistVisible(ob)) return null;
  const done = new Set(ob.tasks || []);
  const allDone = TASKS.every((k) => done.has(k));

  const open = (task) => {
    if (task === "ai") window.dispatchEvent(new CustomEvent(OPEN_AI_EVENT));
    else nav(TASK_ROUTES[task]);
  };

  return (
    <section className="border-b border-border bg-surface px-6 md:px-10 py-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">{t("onboarding.checklistEyebrow", { done: done.size, total: TASKS.length })}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold tracking-tight">
            {allDone ? t("onboarding.checklistDoneTitle") : t("onboarding.checklistTitle")}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => updateOnboarding({ checklist: false })}
          aria-label={t("onboarding.checklistClose")}
          title={t("onboarding.checklistClose")}
          data-testid="onboarding-checklist-close"
          className="p-1 text-text-muted hover:text-white"
        >
          <X size={16} />
        </button>
      </div>
      <ul className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border">
        {TASKS.map((task) => {
          const isDone = done.has(task);
          return (
            <li key={task} className="bg-bg">
              <button
                type="button"
                onClick={() => open(task)}
                data-testid={`onboarding-task-${task}`}
                aria-label={`${t(`onboarding.tasks.${task}`)}${isDone ? ` (${t("onboarding.taskDone")})` : ""}`}
                className="w-full h-full text-left p-4 flex gap-3 items-start hover:bg-surfaceHover"
              >
                <span className={`mt-0.5 w-4 h-4 flex-shrink-0 grid place-items-center border ${isDone ? "bg-signal-green border-signal-green text-black" : "border-zinc-600"}`}>
                  {isDone && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="flex flex-col gap-1">
                  <span className={`text-sm ${isDone ? "text-text-secondary line-through" : "text-white"}`}>{t(`onboarding.tasks.${task}`)}</span>
                  <span className="text-xs text-text-muted">{t(`onboarding.taskHints.${task}`)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
